import WRAP from "WRAP-JS";
import { formatUTCDate, mps2kt } from "../../../utils/FPEUtils";
import { WX_ID_POTEKA } from "../../../store/WeatherContentsState";
import {
  configPotekaAirTempLayer,
  configPotekaDh,
  configPotekaPrecipitationLayer,
  configPotekaRelativeHumidityLayer,
  configPotekaWeatherLayer,
  configPotekaWindDirectionLayer, configPotekaWindSpeedLayer
} from "./config/ConfigPoteka";
import LegendPoteka from "../legend/LegendPoteka";
import TimeController from "../helper/TimeController";
import { nearValidTime } from "../helper/MapUtil";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_POTEKA, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../../../constants/ErrorMessage";

export const POTEKA_TYPE_WIND_DIR = 'WIND_DIR';
export const POTEKA_TYPE_PRECIPITATION = 'PRECIPITATION';
export const POTEKA_TYPE_AIR_TEMP = 'AIR_TEMP';
export const POTEKA_TYPE_WEATHER = 'WEATHER';
export const POTEKA_TYPE_RELATIVE_HUMIDITY = 'RELATIVE_HUMIDITY';

const MISSING_THRESHOLD = {
  [WX_ID_POTEKA]: 1800
};

class Poteka {
  static NAME = WX_ID_POTEKA;

  dataType;
  dh;
  layerWindDirection;
  layerWindSpeed;
  layerPrecipitation;
  layerAirTemp;
  layerWeather;
  layerRelativeHumidity;
  
  ready = false;
  dhAutoUpdate = false;
  visible;
  dispatcher;
  potekaDispatcher;
  validTime = '---';s

  windDirectionVisible;
  windSpeedVisible;

  // タイムコントローラー
  potekaTimeController;
  // タイムコントローラーのリスト
  timeList;
  // 自動更新フラグ
  autoRefresh = true;
  errorMessageInfo;

  constructor(config) {
    this.potekaTimeController = new TimeController(WX_ID_POTEKA, {
      label: 'POTEKA',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.windDirectionVisible = !!config.windSpdDir;
    this.windSpeedVisible = !!config.windSpdDir;
    this.dataType = config.defaultElement || POTEKA_TYPE_AIR_TEMP;

    this.layerWindDirection = new WRAP.Geo.Layer(`${Poteka.NAME}_WIND_DIR`);
    this.layerWindSpeed = new WRAP.Geo.Layer(`${Poteka.NAME}_WIND_SPEED`);
    this.layerPrecipitation = new WRAP.Geo.Layer(`${Poteka.NAME}_PRECIPITATION`);
    this.layerAirTemp = new WRAP.Geo.Layer(`${Poteka.NAME}_AIR_TEMP`);
    this.layerWeather = new WRAP.Geo.Layer(`${Poteka.NAME}_WEATHER`);
    this.layerRelativeHumidity = new WRAP.Geo.Layer(`${Poteka.NAME}_RELATIVE_HUMIDITY`);

    this.layerWindDirection.setVisible(false);
    this.layerWindSpeed.setVisible(false);
    this.layerPrecipitation.setVisible(false);
    this.layerAirTemp.setVisible(false);
    this.layerWeather.setVisible(false);
    this.layerRelativeHumidity.setVisible(false);

    this.layerWindDirection.disableInteraction(true);
    this.layerWindSpeed.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  setPotekaElementInfoDispatcher(dispatcher) {
    this.potekaDispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configPotekaDh);
      this.dh.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
      this.missingDataObserver.monitoring(WX_ID_POTEKA, this.dh, this._getTargetLayer.bind(this));
      this.dh.updated('timelist', ref => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._selectValidTime(ref, validTime => {
          this.dh.load('time', {validtime: validTime}, true, ref => {
            resolve(ref);
          }, () => {
            this.dispatcher({type: 'SHOW_ERROR', payload: {
              message: COMMUNICATE_ERROR_MESSAGE_POTEKA,
              replacement: {data: 'time'},
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
            }});
          });
        });
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_POTEKA,
          replacement: {data: 'timelist'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
      this.dh.updated('master', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_POTEKA,
          replacement: {data: 'master'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });
      this.dh.updated('info', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_POTEKA,
          replacement: {data: 'info'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });
    });
  }

  initLayer() {
    this.layerWindDirection.configure(this.dh, configPotekaWindDirectionLayer);
    this.layerWindSpeed.configure(this.dh, configPotekaWindSpeedLayer);
    const filter = feature => {
      const windSpdMsec = feature.properties.WNDSPD;
      if (windSpdMsec < 0 || isNaN(windSpdMsec)) {
        return false;
      }
      if (isNaN(feature.properties.WNDDIR)) {
        return false;
      }
      feature.properties.WNDSPDKT = Math.round(mps2kt(windSpdMsec));
      feature.properties.WNDSPDKTTEXT = `${feature.properties.WNDSPDKT}kt`
      return this._filteringFeature(feature);
    };
    this.layerWindDirection.setAttributes({
      filter: filter
    });
    this.layerWindSpeed.setAttributes({
      filter: filter
    });
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    }
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    }

    this.layerPrecipitation.setAttributes({filter: this._filteringFeature.bind(this)});
    this.layerAirTemp.setAttributes({filter: this._filteringFeature.bind(this)});
    this.layerWeather.setAttributes({filter: this._filteringFeature.bind(this)});
    this.layerRelativeHumidity.setAttributes({filter: this._filteringFeature.bind(this)});

    this.layerPrecipitation.configure(this.dh, configPotekaPrecipitationLayer);
    this.layerAirTemp.configure(this.dh, configPotekaAirTempLayer);
    this.layerWeather.configure(this.dh, configPotekaWeatherLayer);
    this.layerRelativeHumidity.configure(this.dh, configPotekaRelativeHumidityLayer);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return [
      this.layerWindDirection,
      this.layerWindSpeed,
      this.layerPrecipitation,
      this.layerAirTemp,
      this.layerWeather,
      this.layerRelativeHumidity
    ];
  }

  setVisible(visible) {
    if (!this.ready) return;

    const targetLayer = this._getTargetLayer();

    if (visible) {
      targetLayer.show();
      if (this.windDirectionVisible) {
        this.layerWindDirection.show();
      }
      if (this.windSpeedVisible) {
        this.layerWindSpeed.show();
      }
    } else {
      targetLayer.hide();
      this.layerWindDirection.hide();
      this.layerWindSpeed.hide();
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this.dh.setAutoUpdate('timelist', enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  initTimeController() {
    this.potekaTimeController.addLayer(this.layerAirTemp);
    this.potekaTimeController.addLayer(this.layerPrecipitation);
    this.potekaTimeController.addLayer(this.layerRelativeHumidity);
    this.potekaTimeController.addLayer(this.layerWeather);
    this.potekaTimeController.addLayer(this.layerWindDirection);
    this.potekaTimeController.addLayer(this.layerWindSpeed);
    if (this.visible) {
      this.potekaTimeController.setDisplayTime(this.validTime);
    }
  }

  getTimeController() {
    return this.potekaTimeController;
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;
    this.autoRefresh = autoFlg;
    if (!this.ready) return;
    this.dh.setAutoUpdate('timelist', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  _updateTimeList() {
    let validTimes = this.dh.retained.timelist?.validtime;
    if (!validTimes) return;

    //最新から1時間以内のものにfilter
    validTimes = Array.from(validTimes).sort();
    const latest = new WRAP.Core.DateTime(validTimes.slice(-1)[0]);
    this.timeList = validTimes.filter(vt => {
      const diff = (new WRAP.Core.DateTime(vt)).diff(latest);
      return 0 <= diff && diff <= 3600;
    });

    if (this.autoRefresh) {
      this.potekaTimeController.setCurrentTime(this.validTime);
    }

    this.potekaTimeController.adjustCurrentTime();
    this.potekaTimeController.invalidate();
  }

  _validateTime(data, displayTime, content) {
    const vt = nearValidTime(data.retained.timelist.validtime, displayTime);

    if (vt) {
      this.validTime = content.validtime = vt;
      this.dh.controller.loadTime(this.dh, this.validTime);
      return true;
    }
    return false;
  }

  changeRoutePointEditing(routePointEditing) {
    const layer = this._getTargetLayer();
    if (layer === this.layerWindDirection || layer === this.layerWindSpeed) {
      return;
    }
    layer.disableInteraction(routePointEditing);
  }

  getTimeInformation() {
    return {
      id: Poteka.NAME,
      label: 'POTEKA',
      baseTime: {
        id: Poteka.NAME,
        time: this.validTime
      }
    }
  }

  getLegend() {
    return {
      id: Poteka.NAME,
      label: 'POTEKA',
      component: LegendPoteka,
      contents: this
    };
  }

  setSpecificPotekaIds(specificPotekaIds) {
    this.specificPotekaIds = specificPotekaIds;
  }

  _filteringFeature(feature) {
    /*
     * OPEN 0 or Contract設定の「特定のPOTEKA地点」の指定にmatch
     * and
     * END AVAIRAVLEか未来
     */
    const {OPEN: open, END: end, LCLID: lclId} = feature.properties;
    if (end === 'AVAILABLE' || formatUTCDate(new Date(), '%Y-%m-%d') <= end) {
      if (open === 0) {
        return true;
      } else {
        if (this.specificPotekaIds?.length) {
          const m = lclId.match(/^POTEKA_(.*)$/);
          if (m) {
            const id = +m[1];
            return this.specificPotekaIds.includes(id);
          }
        }
      }
    }
    return false;
  }

  getDataType() {
    return this.dataType;
  }

  setDataType(dataType) {
    if (dataType === this.dataType) return;

    //現在のdataTypeを非表示
    this.setVisible(false);
    this.dataType = dataType;
    //切り替え後のdataTypeを表示
    this.setVisible(true);
    this.potekaDispatcher(dataType);
  }

  getWindDirectionVisible() {
    return this.windDirectionVisible;
  }

  getWindSpeedVisible() {
    return this.windSpeedVisible;
  }

  setWindDirectionVisible(visible) {
    if (visible === this.windDirectionVisible) return;

    this.windDirectionVisible = visible;
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    } else {
      this.layerWindDirection.hide();
    }
  }

  setWindSpeedVisible(visible) {
    if (visible === this.windSpeedVisible) return;

    this.windSpeedVisible = visible;
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    } else {
      this.layerWindSpeed.hide();
    }
  }

  _selectValidTime(ref, cb) {
    let targetTime;
    if (!this.autoRefresh) {
      targetTime = this.potekaTimeController.getCurrentTime();
    } else {
      targetTime = Date.now();
    }

    const vt = nearValidTime(ref.validtime, targetTime);
    if (this.validTime !== vt) {
      this.validTime = vt;
      cb(vt);
    }

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  _getTargetLayer() {
    switch (this.dataType) {
      case POTEKA_TYPE_WIND_DIR:
        return this.layerWindDirection;
      case POTEKA_TYPE_PRECIPITATION:
        return this.layerPrecipitation;
      case POTEKA_TYPE_AIR_TEMP:
        return this.layerAirTemp;
      case POTEKA_TYPE_WEATHER:
        return this.layerWeather;
      case POTEKA_TYPE_RELATIVE_HUMIDITY:
        return this.layerRelativeHumidity;
      default:
    }
  }
}

export default Poteka;